<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue';
import loadGoogleMaps from '@/utils/load-google-maps';
import * as Sentry from '@sentry/vue';
import GMAP_KEY_MAPPING from '@/constants';

const emit = defineEmits(['update-address', 'update-validation']);
const props = defineProps<{
    rc?: string;
}>();

const autocompleteElement = ref<HTMLElement | null>(null);
const address = ref<any>(null);
const isValid = ref<Boolean | null>(null);

async function onPlaceChanged(event: any) {
    try {
        // Check if event and placePrediction exist
        if (!event?.placePrediction) {
            address.value = null;
            isValid.value = false;
            emit('update-address', null);
            emit('update-validation', false);
            return;
        }

        // Convert prediction to place and fetch details
        const place = event.placePrediction.toPlace();
        await place.fetchFields({
            fields: ['addressComponents', 'location'],
        });

        if (!place.addressComponents || !place.location) {
            // Invalid selection
            address.value = null;
            isValid.value = false;
            emit('update-address', null);
            emit('update-validation', false);
            return;
        }

        // Parse address components
        const addressData = {
            streetNumber: '',
            streetName: '',
            city: '',
            state: '',
            postalCode: '',
            latitude: place.location.lat(),
            longitude: place.location.lng(),
        };

        place.addressComponents.forEach((component: any) => {
            const { types } = component;

            if (types.includes('street_number')) {
                addressData.streetNumber = component.longText;
            }
            if (types.includes('route')) {
                addressData.streetName = component.longText;
            }
            if (types.includes('locality')) {
                addressData.city = component.longText;
            }
            if (types.includes('administrative_area_level_1')) {
                addressData.state = component.shortText;
            }
            if (types.includes('postal_code')) {
                addressData.postalCode = component.longText;
            }
        });

        // Validate address completeness
        const isComplete =
            !!addressData.streetNumber &&
            !!addressData.streetName &&
            !!addressData.city &&
            !!addressData.state &&
            !!addressData.postalCode;

        address.value = isComplete ? addressData : null;
        isValid.value = isComplete;

        emit('update-address', address.value);
        emit('update-validation', isComplete);
    } catch (error) {
        // Handle any errors in place selection or fetching
        Sentry.captureMessage(`Error processing place selection: ${error}`, 'error');
        address.value = null;
        isValid.value = false;
        emit('update-address', null);
        emit('update-validation', false);
    }
}

function initializeAutocomplete() {
    if (!(window as any).google?.maps?.places?.PlaceAutocompleteElement || !autocompleteElement.value) {
        return;
    }

    // Create the Place Autocomplete Element
    const placeAutocomplete = new (window as any).google.maps.places.PlaceAutocompleteElement({
        includedRegionCodes: ['US'],
    });

    // Default to light mode
    placeAutocomplete.style.colorScheme = 'light';

    // Append to our container
    autocompleteElement.value.appendChild(placeAutocomplete);

    // Listen for place selection
    placeAutocomplete.addEventListener('gmp-select', onPlaceChanged);
}

onMounted(async () => {
    let gMapApiKey;
    if (props.rc && window.location.hostname && `${props.rc}:${window.location.hostname}` in GMAP_KEY_MAPPING) {
        // @ts-ignore
        gMapApiKey = import.meta.env[GMAP_KEY_MAPPING[`${props.rc}:${window.location.hostname}`]];
    } else {
        // @ts-ignore
        gMapApiKey = import.meta.env.VITE_GMAP_API_KEY;
    }

    // Load Google Maps places library using existing utility
    await loadGoogleMaps(gMapApiKey, 'places');

    // Wait for next tick to ensure DOM is ready
    await nextTick();

    // Initialize the autocomplete widget
    initializeAutocomplete();
});
</script>

<template>
    <div class="autocomplete-widget">
        <label
            id="address-label"
            class="font-size-sm"
            for="autocompleteElement">
            Address
        </label>
        <!-- Container where the Google Place Autocomplete Element will be appended -->
        <div
            id="autocompleteElement"
            ref="autocompleteElement"
            class="autocomplete-container"
            :style="{ '--gmp-border-color': isValid === false ? '#DE3C50' : '#222633' }"
            aria-labelledby="address-label"
            aria-describedby="address-help"></div>
        <div
            v-if="isValid === false"
            id="address-error"
            class="text-danger font-size-sm mt-25"
            role="alert"
            aria-live="polite">
            Please select a complete street address from the dropdown. If you continue to have issues, contact
            <span><a href="mailto:solarteam@energysage.com">solarteam@energysage.com</a></span> and our team will
            follow up.
        </div>

        <!-- Hidden help text -->
        <div
            id="address-help"
            class="sr-only">
            Type your address and select from dropdown suggestions
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use '@energysage/es-ds-styles/scss/variables' as variables;

:deep(gmp-place-autocomplete) {
    border: 1px solid var(--gmp-border-color, #{variables.$gray-900});
    border-radius: variables.$border-radius-xs;
}
</style>
